import React, { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import Script from 'next/script';
import { useFeature } from 'context/growthbook/useFeature';
import { isAsyncRoute, isNonAsyncRoute } from 'utils/routes';
import { useChatTracking } from 'hooks/useChatTracking';
import { useReservation } from '@updater/ui-widgets';
import { useUserData } from 'flows/core/hooks/use-profile';

const script = `window.lpTag=window.lpTag||{},'undefined'==typeof window.lpTag._tagCount?(window.lpTag={wl:lpTag.wl||null,scp:lpTag.scp||null,site:'57896177'||'',section:lpTag.section||'',tagletSection:lpTag.tagletSection||null,autoStart:lpTag.autoStart!==!1,ovr:lpTag.ovr||{},_v:'1.10.0',_tagCount:1,protocol:'https:',events:{bind:function(t,e,i){lpTag.defer(function(){lpTag.events.bind(t,e,i)},0)},trigger:function(t,e,i){lpTag.defer(function(){lpTag.events.trigger(t,e,i)},1)}},defer:function(t,e){0===e?(this._defB=this._defB||[],this._defB.push(t)):1===e?(this._defT=this._defT||[],this._defT.push(t)):(this._defL=this._defL||[],this._defL.push(t))},load:function(t,e,i){var n=this;setTimeout(function(){n._load(t,e,i)},0)},_load:function(t,e,i){var n=t;t||(n=this.protocol+'//'+(this.ovr&&this.ovr.domain?this.ovr.domain:'lptag.liveperson.net')+'/tag/tag.js?site='+this.site);var o=document.createElement('script');o.setAttribute('charset',e?e:'UTF-8'),i&&o.setAttribute('id',i),o.setAttribute('src',n),document.getElementsByTagName('head').item(0).appendChild(o)},init:function(){this._timing=this._timing||{},this._timing.start=(new Date).getTime();var t=this;window.attachEvent?window.attachEvent('onload',function(){t._domReady('domReady')}):(window.addEventListener('DOMContentLoaded',function(){t._domReady('contReady')},!1),window.addEventListener('load',function(){t._domReady('domReady')},!1)),'undefined'===typeof window._lptStop&&this.load()},start:function(){this.autoStart=!0},_domReady:function(t){this.isDom||(this.isDom=!0,this.events.trigger('LPT','DOM_READY',{t:t})),this._timing[t]=(new Date).getTime()},vars:lpTag.vars||[],dbs:lpTag.dbs||[],ctn:lpTag.ctn||[],sdes:lpTag.sdes||[],hooks:lpTag.hooks||[],identities:lpTag.identities||[],ev:lpTag.ev||[]},lpTag.init()):window.lpTag._tagCount+=1;`;

interface LivePersonChatScriptProps { }

/**
 * Creates dynamic sections array based on route and experiment values
 */
export const createSections = (
  pathname: string,
  asyncExperimentEnabled: boolean,
  nonAsyncExperimentEnabled: boolean
): string[] => {
  const sections: string[] = [];

  if (asyncExperimentEnabled && isAsyncRoute(pathname)) {
    sections.push('tvi-async-buyflow');
  } else if (nonAsyncExperimentEnabled && isNonAsyncRoute(pathname)) {
    sections.push('tvi-non-async');
  }

  return sections;
};


const LivePersonChatScript: React.FC<LivePersonChatScriptProps> = () => {
  const router = useRouter();
  const { bindLivePersonEvents } = useChatTracking();
  const [hasBindedLivePersonEvents, setHasBindedLivePersonEvents] = useState(false);

  const {
    value: asyncBuyflowEnabled,
    loading: loadingAsyncChatFlag,
  } = useFeature('moving_app_tvi_chat_async_buyflow');

  const {
    value: nonAsyncEnabled,
    loading: loadingNonAsyncChatFlag,
  } = useFeature('moving_app_tvi_chat_non_async');

  // Get reservation data for TVI flows to pass lead/reservation code to agents
  const { user } = useUserData();
  const reservationData = useReservation({
    moveId: user?.currentMove?.id ?? '',
    skip: !user?.currentMove?.id,
  });

  const lpTag = typeof window !== 'undefined' ? window?.lpTag : undefined;
  const reservationCode = reservationData?.reservationInfo?.code;

  useEffect(() => {
    if (lpTag && !hasBindedLivePersonEvents) {
      bindLivePersonEvents();
      setHasBindedLivePersonEvents(true);
    }
  }, [bindLivePersonEvents, lpTag]);

  useEffect(() => {
    if (lpTag) {
      // Set up SDE (Structured Data Entity) to pass reservation/lead code to agents
      lpTag.sdes = lpTag.sdes ?? [];

      if (reservationCode) {
        lpTag.sdes.push({
          type: 'lead',
          lead: {
            leadId: reservationCode,
          },
        });
      }
    }
  }, [reservationCode, lpTag]);

  useEffect(() => {
    if (loadingAsyncChatFlag || loadingNonAsyncChatFlag) {
      return;
    }

    if (lpTag) {
      const url = window.location.href;

      // Create dynamic sections
      const sections = createSections(router.pathname, asyncBuyflowEnabled, nonAsyncEnabled);
      lpTag.section = sections;
      /*
       * LivePerson, by default, will not know about URL updates unless there's
       * a page refresh. This ensures that page rules get applied properly on
       * all pages with dynamic sections and experiment data.
       */
      lpTag?.newPage?.(url, {
        taglets: {
          rendererStub: {
            divIdsToKeep: {
              divId: true,
            },
          },
        },
      });
    }
  }, [
    router.pathname,
    asyncBuyflowEnabled,
    nonAsyncEnabled,
    loadingAsyncChatFlag,
    loadingNonAsyncChatFlag,
    lpTag
  ]);

  return (
    <Script
      id="live-person-script"
      type="text/javascript"
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{
        __html: script,
      }}
    />
  );
};

export default LivePersonChatScript;
